import { useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate, Link } from "react-router-dom";
import { fingerscan } from "careplix-scan-sdk";
import Analyzing from "../assets/images/analyzingNew.webp";
import Failure from "../assets/images/failureNew.webp";
import NoCamera from "../assets/images/no-cameraNew.webp";
import BackArrow from "../assets/images/back-arrow.svg";
import Logo from "../assets/images/e&enterprise_logo_white.png";
import { api_key, authEmployeeID, sdkURL } from "../utils/constants";
import { useAuth } from "../utils/auth";

const PulmoFingerScan = () => {
  const {
    userData: { gender, dob },
  } = useAuth();
  const navigate = useNavigate();
  const { state } = useLocation();

  const videoRef = useRef();
  const canvasRef = useRef();

  const [loading, setLoading] = useState(true);
  const [analyzing, setAnalyzing] = useState(false);
  const [noCameraError, setNoCameraError] = useState(false);
  const [error, setError] = useState("");
  const [scanFrameData, setScanFrameData] = useState({
    type: "error",
    message: "---",
    progress: 0,
    timeElapsed: 0,
  });
  const causeList = useRef([]);

  useEffect(() => {
    setLoading(true);
    fingerscan.onFrame(setScanFrameData);
    fingerscan.onError((err) => {
      console.error(err);
      if (
        err.message ===
        "We are not able to access the Camera. Please try again."
      )
        setNoCameraError(true);
      else setError(err.message);
    });
    fingerscan.onScanFinish(
      async ({ raw_intensity, ppg_time, average_fps }) => {
        try {
          setAnalyzing(true);
          const addScanResp = await fetch(sdkURL("/vitals/add-scan"), {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({
              api_key,
              scan_token: state?.scanToken ?? "",
              posture: "resting",
              dob: dob,
              gender: gender,
              metadata: {
                ppg_time: ppg_time,
                raw_intensity: raw_intensity,
                device: `RPPG_CAREPLIX_FINGER_${
                  /iPhone|iPad|iPod|Macintosh|Mac/i.test(navigator.userAgent)
                    ? "IOS"
                    : "ANDROID"
                }`,
                fps: average_fps,
              },
              notes: { app_code: "CPPULMO", client_code: "CPUNITY" },
            }),
          });
          const addScanRespJSON = await addScanResp.json();
          if (addScanRespJSON?.statusCode?.toString().startsWith("2"))
            navigate("/voice-audio-sample", { state, replace: true });
          else
            throw new Error(
              addScanRespJSON?.message ?? "Failed to Fetch Result"
            );
        } catch (err) {
          console.error(err);
          setError(err?.message ?? "Some error occurred.");
        }
      }
    );
    if (canvasRef.current !== null && videoRef.current !== null) {
      fingerscan
        .startScan({
          scanDuration: 30,
          canvasElement: canvasRef.current,
          videoElement: videoRef.current,
        })
        .then(() => console.log("Scan Started"))
        .catch(() => console.log("Scan Failed"))
        .finally(() => setLoading(false));
    }
    return () => {
      fingerscan.stopScan();
    };
  }, [navigate, state]);

  const scanMessages = useMemo(
    () =>
      [
        "We burn calories while we are asleep because brain activity requires energy",
        "Exercise promotes cardiovascular health, ensuring more blood and oxygen to circulate the body, helping to elevate energy",
        "Always sleep on your back straight since it allows your neck and spine in a neutral position",
        "Alcohol consumption disrupts chemical impulses between brain cells causing impulsive behaviour, slurred speech, impaired memory",
        "Meditation increases nitric oxide generation in the brain helping to lower your blood pressure",
        "Meditation reduces the production of cytokines in several recent studies, thus helping in stress reduction",
        "Laughing is good for your heart. It reduces stress and gives a boost to your immune system",
        "Lack of sleep can lead to heart disease, heart attack, or stroke",
        "Heart pumps about 2,000 gallons of blood every day",
        "Your heart will beat about 115,000 times each day",
      ].sort(() => Math.random() - 0.5),
    []
  );

  const currentMessage = useMemo(() => {
    if (scanFrameData.type === "error") return "---";
    const timeElapsed = scanFrameData.timeElapsed / 1000;
    if (timeElapsed <= 2.5) return "Cover your back camera with your finger";
    else if (timeElapsed <= 5)
      return "During the measurement, please do not speak or move";
    else if (timeElapsed <= 7.5)
      return "Keep your device steady throughout the vital measurement";
    else if (timeElapsed <= 10) return scanMessages[0];
    else if (timeElapsed <= 12.5) return scanMessages[1];
    else if (timeElapsed <= 15) return scanMessages[2];
    else if (timeElapsed <= 17.5) return scanMessages[3];
    else if (timeElapsed <= 20) return scanMessages[4];
    else if (timeElapsed <= 22.5) return scanMessages[5];
    else if (timeElapsed <= 25) return scanMessages[6];
    else if (timeElapsed <= 27.5) return scanMessages[7];
    else if (timeElapsed <= 30) return scanMessages[8];
    else if (timeElapsed <= 35) return scanMessages[9];
  }, [scanFrameData.timeElapsed, scanFrameData.type, scanMessages]);

  return (
    <main className="h-dvh w-dvw overflow-hidden flex flex-col bg-gradient-to-b from-primary to-transparent to-50%">
      <section className="mt-[5rem] mx-5 rounded-[20px] bg-[#A3A2A8] tab-screen responsive-margin-top">
        <div className="relative h-[calc(100dvh-18.85rem)] rounded-[20px] overflow-hidden">
          <video
            ref={videoRef}
            id="videoInput"
            className="fixed top-4 right-4 h-px w-px"
            autoPlay
            muted
            playsInline
          />
          <canvas
            ref={canvasRef}
            id="canvasOutput"
            className="w-full h-full -scale-x-100"
          />

          {scanFrameData.type === "scan" && (
            <div className="fixed left-[20px] right-[20px] tab-screen h-12 animate-scan from-[#41a0e1]/65" />
          )}

          <div className="fixed top-0 left-0 right-0 p-6 lg:px-0 text-white flex items-center justify-between tab-screen bg-primary lg:bg-transparent md:bg-transparent">
            <div className="flex items-center">
              <Link
                to={-1}
                className="w-9 h-9 bg-[#2D2D2D] rounded-full flex items-center justify-center"
              >
                <img src={BackArrow} alt="" />
              </Link>
              <h2 className="grow text-left lg:text-xl md:text-xl text-base font-medium text-white lg:ml-6 md:ml-6 ml-4">
                Finger Scan
              </h2>
            </div>
            <img src={Logo} className="lg:h-9 md:h-9 h-4" alt="" />
          </div>

          {scanFrameData.type === "error" && (
            <p className="fixed top-[40vh] -translate-y-1/2 left-14 right-14 px-1 py-0.5 rounded-full bg-error/70 text-white text-center text-sm font-medium responsive-not-connect">
              {scanFrameData.message}
            </p>
          )}
        </div>
      </section>
      <div className="fixed bottom-0 left-0 right-0 min-h-52 flex flex-col bg-primary text-white tab-screen">
        <div className="relative shrink-0 h-[5px] mx-5 lg:mx-0 md:mx-0 border-y border-primary bg-white rounded-full">
          <div
            className="absolute left-0 top-0 bottom-0 rounded-r-full bg-secondary shadow-inner transition-all duration-1000"
            style={{ width: `${scanFrameData.progress}%` }}
          />
        </div>
        <div className="relative grow m-4 lg:mx-0 md:mx-0 p-4 pb-12 rounded-2xl bg-[#364560]">
          <div>
            <h4 className="font-semibold">
              {scanFrameData.type === "error"
                ? "Scan Stopped"
                : scanFrameData.message}
            </h4>
            <h6 className="mt-2.5 text-xs leading-tight tracking-tight">
              {currentMessage}
            </h6>
          </div>
          <button
            type="button"
            onClick={() => {
              fingerscan.stopScan();
              fetch(sdkURL("/vitals/update-scan-status"), {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({
                  api_key,
                  scan_token: state?.scanToken,
                  employee_id: authEmployeeID,
                  fire_type: "on_halt",
                  fire_reason: "cancel-scan",
                  error_list: causeList.current,
                }),
              })
                .then((resp) => resp.json())
                .then((json) => {
                  if (!json.statusCode?.toString().startsWith("2"))
                    throw new Error(
                      json.message ?? "Unable to send Error logs."
                    );
                })
                .catch(console.error);
              navigate(-1);
            }}
            className="absolute bottom-4 right-4 text-secondary text-xs font-medium"
          >
            &lt;&nbsp;Cancel Scan
          </button>
        </div>
      </div>

      {analyzing && (
        <div className="fixed top-0 bottom-0 left-0 right-0 px-8 py-12 flex flex-col items-center justify-center bg-primary text-white text-center">
          <img className="shrink-0 h-32" src={Analyzing} alt="Analyzing..." />
          <h4 className="mt-8 text-xl font-bold">Analyzing Data</h4>
          <p className="mt-4 font-light">
            Continuing analysis for
            <br />
            accurate measurements...
          </p>
        </div>
      )}

      {error.length > 0 && (
        <div className="fixed top-0 bottom-0 left-0 right-0 px-8 py-12 flex flex-col items-center justify-center bg-primary text-white text-center">
          <img className="shrink-0 h-44" src={Failure} alt="Failure..." />
          <p className="mt-8 text-xl font-bold">
            Sorry, we failed to calculate
            <br />
            your measurements
          </p>
          <p className="mt-4 max-h-40 overflow-auto whitespace-pre-line font-light">
            {error}
          </p>
          <button
            className={`mt-8 w-60 px-5 py-2.5 rounded-full bg-white text-primary font-medium${
              error === "You have exceeded daily scan limit."
                ? " invisible pointer-events-none -mb-16"
                : ""
            }`}
            type="button"
            onClick={() => {
              fingerscan.stopScan();
              navigate(-1);
            }}
          >
            Try Again
          </button>
          <button
            className="mt-4 text-white text-sm font-medium"
            type="button"
            onClick={() => {
              fingerscan.stopScan();
              navigate(-3);
            }}
          >
            Back to Dashboard
          </button>
        </div>
      )}

      {noCameraError && (
        <div className="fixed top-0 bottom-0 left-0 right-0 px-8 py-12 flex flex-col items-center justify-center bg-primary text-white text-center">
          <img className="shrink-0 h-44" src={NoCamera} alt="No Camera..." />
          <p className="mt-8 text-xl font-bold">
            No Camera
            <br />
            Permission Found
          </p>
          <p className="mt-4 max-h-40 overflow-auto whitespace-pre-line font-light">
            We are not able to access the Camera. Please try again.
          </p>
          <button
            className="mt-8 px-5 py-2.5 rounded-full bg-gradient text-white font-medium"
            type="button"
            onClick={() => {
              fingerscan.stopScan();
              navigate("/camera-permission-guide", { replace: true });
            }}
          >
            How to give camera permission?
          </button>
          <button
            className="mt-4 text-white text-sm font-medium"
            type="button"
            onClick={() => {
              fingerscan.stopScan();
              navigate(-1);
            }}
          >
            Back to Dashboard
          </button>
        </div>
      )}

      {loading && (
        <div
          className="min-h-screen flex justify-center items-center"
          style={{}}
        >
          <svg className="w-40" viewBox="0 0 100 100">
            <g transform="translate(25 50)">
              <circle cx="0" cy="0" r="6" className="fill-secondary">
                <animateTransform
                  attributeName="transform"
                  type="scale"
                  begin="-0.3333333333333333s"
                  calcMode="spline"
                  keySplines="0.3 0 0.7 1;0.3 0 0.7 1"
                  values="0;1;0"
                  keyTimes="0;0.5;1"
                  dur="1s"
                  repeatCount="indefinite"
                ></animateTransform>
              </circle>
            </g>
            <g transform="translate(50 50)">
              <circle cx="0" cy="0" r="6" className="fill-[#07255f]">
                <animateTransform
                  attributeName="transform"
                  type="scale"
                  begin="-0.16666666666666666s"
                  calcMode="spline"
                  keySplines="0.3 0 0.7 1;0.3 0 0.7 1"
                  values="0;1;0"
                  keyTimes="0;0.5;1"
                  dur="1s"
                  repeatCount="indefinite"
                ></animateTransform>
              </circle>
            </g>
            <g transform="translate(75 50)">
              <circle cx="0" cy="0" r="6" className="fill-secondary">
                <animateTransform
                  attributeName="transform"
                  type="scale"
                  begin="0s"
                  calcMode="spline"
                  keySplines="0.3 0 0.7 1;0.3 0 0.7 1"
                  values="0;1;0"
                  keyTimes="0;0.5;1"
                  dur="1s"
                  repeatCount="indefinite"
                ></animateTransform>
              </circle>
            </g>
          </svg>
        </div>
      )}
    </main>
  );
};

export default PulmoFingerScan;
